import JSZip from 'jszip';
import moment from 'moment';
import { buildTermSheetPreviewParagraphs } from './buildTermSheetClauses';
import { stripPreviewInputMarkers } from './previewInput';

const ODT_MIME = 'application/vnd.oasis.opendocument.text';

function escapeXml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function textParagraph(text, styleName = 'Body') {
  return `<text:p text:style-name="${styleName}">${escapeXml(text)}</text:p>`;
}

function buildTermSheetContentXml({ fields, name, customerName, sequenceNumber }) {
  const paragraphs = buildTermSheetPreviewParagraphs(fields);
  const sectionRows = paragraphs
    .map(
      (paragraph) => `<table:table-row>
      <table:table-cell table:style-name="SectionCell">${textParagraph(paragraph.title, 'SectionTitle')}</table:table-cell>
      <table:table-cell table:style-name="TermsCell">${stripPreviewInputMarkers(paragraph.text)
        .split('\n\n')
        .map((part) => textParagraph(part, 'Body'))
        .join('')}</table:table-cell>
    </table:table-row>`
    )
    .join('');

  const metaLines = [
    textParagraph(`Term sheet: ${name || 'Draft'}`, 'Subtitle'),
    customerName ? textParagraph(`Customer: ${customerName}`, 'Meta') : '',
    sequenceNumber != null ? textParagraph(`Number: #${sequenceNumber}`, 'Meta') : '',
  ].join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
  xmlns:table="urn:oasis:names:tc:opendocument:xmlns:table:1.0"
  xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"
  xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"
  office:version="1.3">
  <office:automatic-styles>
    <style:style style:name="Title" style:family="paragraph">
      <style:text-properties fo:font-size="20pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Subtitle" style:family="paragraph">
      <style:text-properties fo:font-size="12pt"/>
    </style:style>
    <style:style style:name="Meta" style:family="paragraph">
      <style:text-properties fo:font-size="10pt"/>
    </style:style>
    <style:style style:name="SectionTitle" style:family="paragraph">
      <style:text-properties fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Body" style:family="paragraph"/>
    <style:style style:name="SectionCell" style:family="table-cell">
      <style:table-cell-properties fo:padding="0.08in"/>
    </style:style>
    <style:style style:name="TermsCell" style:family="table-cell">
      <style:table-cell-properties fo:padding="0.08in"/>
    </style:style>
  </office:automatic-styles>
  <office:body>
    <office:text>
      ${textParagraph('Term Sheet', 'Title')}
      ${metaLines}
      ${textParagraph('', 'Body')}
      <table:table table:name="TermSheetSections">
        <table:table-column table:style-name="SectionColumn"/>
        <table:table-column table:style-name="TermsColumn"/>
        ${sectionRows}
      </table:table>
    </office:text>
  </office:body>
</office:document-content>`;
}

const STYLES_XML = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0"
  xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0"
  xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0"
  office:version="1.3">
  <office:styles>
    <style:default-style style:family="paragraph">
      <style:text-properties style:font-name="Arial" fo:font-size="11pt"/>
    </style:default-style>
  </office:styles>
</office:document-styles>`;

const META_XML = `<?xml version="1.0" encoding="UTF-8"?>
<office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0"
  xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0"
  office:version="1.3">
  <office:meta>
    <meta:generator>Twelve Term Sheet</meta:generator>
  </office:meta>
</office:document-meta>`;

const MANIFEST_XML = `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0">
  <manifest:file-entry manifest:media-type="${ODT_MIME}" manifest:full-path="/"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="content.xml"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="styles.xml"/>
  <manifest:file-entry manifest:media-type="text/xml" manifest:full-path="meta.xml"/>
</manifest:manifest>`;

export function buildTermSheetDriveFilename({
  customerName = 'term-sheet',
  name = 'draft',
  sequenceNumber = null,
  extension = '.odt',
  exportDate = moment().format('MM-DD-YYYY'),
}) {
  const parts = [(customerName || 'term-sheet').trim(), (name || 'draft').trim()];
  if (sequenceNumber != null) {
    parts.push(String(sequenceNumber));
  }
  const safeBase = parts
    .join('-')
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`;
  return `${safeBase || 'term-sheet'}-${exportDate}${normalizedExtension}`;
}

export async function buildTermSheetOdtBlob({
  fields,
  name,
  customerName,
  sequenceNumber = null,
}) {
  const zip = new JSZip();
  zip.file('mimetype', ODT_MIME, { compression: 'STORE' });
  zip.file(
    'content.xml',
    buildTermSheetContentXml({ fields, name, customerName, sequenceNumber })
  );
  zip.file('styles.xml', STYLES_XML);
  zip.file('meta.xml', META_XML);
  zip.folder('META-INF')?.file('manifest.xml', MANIFEST_XML);

  const blob = await zip.generateAsync({
    type: 'blob',
    mimeType: ODT_MIME,
    compression: 'DEFLATE',
  });

  return {
    blob,
    filename: buildTermSheetDriveFilename({ customerName, name, sequenceNumber }),
  };
}
